/**
 * useAccountSettingsUIState Hook
 * 
 * Consolidates all UI state management for AccountSettings page.
 */

import { useState } from "react";
import type React from "react";
import { Account, AccountGroupDetailField, AccountDetailValue } from "../../../types";

export interface AccountGroup {
  id: string;
  name: string;
  description?: string;
  order: number;
  createdAt: Date | any;
  details?: AccountGroupDetailField[];
}

export interface UseAccountSettingsUIStateReturn {
  // Group dialog states
  addDialogOpen: boolean;
  setAddDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editDialogOpen: boolean;
  setEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Group form states
  editingGroup: AccountGroup | null;
  setEditingGroup: React.Dispatch<React.SetStateAction<AccountGroup | null>>;
  newGroupName: string;
  setNewGroupName: React.Dispatch<React.SetStateAction<string>>;
  newGroupDescription: string;
  setNewGroupDescription: React.Dispatch<React.SetStateAction<string>>;
  groupNameError: string;
  setGroupNameError: React.Dispatch<React.SetStateAction<string>>;
  editGroupName: string;
  setEditGroupName: React.Dispatch<React.SetStateAction<string>>;
  editGroupDescription: string;
  setEditGroupDescription: React.Dispatch<React.SetStateAction<string>>;
  groupToDelete: string;
  setGroupToDelete: React.Dispatch<React.SetStateAction<string>>;
  isAddingGroup: boolean;
  setIsAddingGroup: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Group menu states
  groupMenuAnchor: HTMLElement | null;
  setGroupMenuAnchor: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  selectedGroup: AccountGroup | null;
  setSelectedGroup: React.Dispatch<React.SetStateAction<AccountGroup | null>>;
  selectedGroupInUse: boolean;
  setSelectedGroupInUse: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Group details dialog states
  detailsDialogOpen: boolean;
  setDetailsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editingGroupDetails: AccountGroupDetailField[];
  setEditingGroupDetails: React.Dispatch<React.SetStateAction<AccountGroupDetailField[]>>;
  originalGroupDetails: AccountGroupDetailField[];
  setOriginalGroupDetails: React.Dispatch<React.SetStateAction<AccountGroupDetailField[]>>;
  editingGroupName: string;
  setEditingGroupName: React.Dispatch<React.SetStateAction<string>>;
  
  // Detail form states
  newDetailName: string;
  setNewDetailName: React.Dispatch<React.SetStateAction<string>>;
  newDetailType: "text" | "string" | "number" | "list" | "";
  setNewDetailType: React.Dispatch<React.SetStateAction<"text" | "string" | "number" | "list" | "">>;
  newListItems: string[];
  setNewListItems: React.Dispatch<React.SetStateAction<string[]>>;
  newDetailDescription: string;
  setNewDetailDescription: React.Dispatch<React.SetStateAction<string>>;
  detailNameError: string;
  setDetailNameError: React.Dispatch<React.SetStateAction<string>>;
  editingDetailIndex: number | null;
  setEditingDetailIndex: React.Dispatch<React.SetStateAction<number | null>>;
  editingOriginalDetail: AccountGroupDetailField | null;
  setEditingOriginalDetail: React.Dispatch<React.SetStateAction<AccountGroupDetailField | null>>;
  
  // Account dialog states
  editAccountDialogOpen: boolean;
  setEditAccountDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  deleteAccountDialogOpen: boolean;
  setDeleteAccountDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Account form states
  editingAccount: Account | null;
  setEditingAccount: React.Dispatch<React.SetStateAction<Account | null>>;
  accountFormData: {
    accountName: string;
    group: string;
    amount: string;
    currency: string;
    description: string;
    details?: AccountDetailValue;
  };
  setAccountFormData: React.Dispatch<React.SetStateAction<{
    accountName: string;
    group: string;
    amount: string;
    currency: string;
    description: string;
    details?: AccountDetailValue;
  }>>;
  accountNameError: string;
  setAccountNameError: React.Dispatch<React.SetStateAction<string>>;
  isSavingAccount: boolean;
  setIsSavingAccount: React.Dispatch<React.SetStateAction<boolean>>;
  creatingAccountName: string | null;
  setCreatingAccountName: React.Dispatch<React.SetStateAction<string | null>>;
  showAccountDetails: boolean;
  setShowAccountDetails: React.Dispatch<React.SetStateAction<boolean>>;
  initialShowAccountDetails: boolean;
  setInitialShowAccountDetails: React.Dispatch<React.SetStateAction<boolean>>;
  expandedAccountDetails: Set<string>;
  setExpandedAccountDetails: React.Dispatch<React.SetStateAction<Set<string>>>;
  initialAccountFormData: {
    accountName: string;
    group: string;
    amount: string;
    currency: string;
    description: string;
    details?: AccountDetailValue;
  } | null;
  setInitialAccountFormData: React.Dispatch<React.SetStateAction<{
    accountName: string;
    group: string;
    amount: string;
    currency: string;
    description: string;
    details?: AccountDetailValue;
  } | null>>;
  
  // Account menu states
  accountMenuAnchor: HTMLElement | null;
  setAccountMenuAnchor: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  selectedAccount: Account | null;
  setSelectedAccount: React.Dispatch<React.SetStateAction<Account | null>>;
  
  // Snackbar states
  snackbarOpen: boolean;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  snackbarMessage: string;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  snackbarElement: React.ReactElement | null;
  setSnackbarElement: React.Dispatch<React.SetStateAction<React.ReactElement | null>>;
}

/**
 * Hook for managing all UI state in AccountSettings
 */
export const useAccountSettingsUIState = (): UseAccountSettingsUIStateReturn => {
  // Group dialog states
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  
  // Group form states
  const [editingGroup, setEditingGroup] = useState<AccountGroup | null>(null);
  const [newGroupName, setNewGroupName] = useState<string>("");
  const [newGroupDescription, setNewGroupDescription] = useState<string>("");
  const [groupNameError, setGroupNameError] = useState<string>("");
  const [editGroupName, setEditGroupName] = useState<string>("");
  const [editGroupDescription, setEditGroupDescription] = useState<string>("");
  const [groupToDelete, setGroupToDelete] = useState<string>("");
  const [isAddingGroup, setIsAddingGroup] = useState<boolean>(false);
  
  // Group menu states
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<AccountGroup | null>(null);
  const [selectedGroupInUse, setSelectedGroupInUse] = useState<boolean>(false);
  
  // Group details dialog states
  const [detailsDialogOpen, setDetailsDialogOpen] = useState<boolean>(false);
  const [editingGroupDetails, setEditingGroupDetails] = useState<AccountGroupDetailField[]>([]);
  const [originalGroupDetails, setOriginalGroupDetails] = useState<AccountGroupDetailField[]>([]);
  const [editingGroupName, setEditingGroupName] = useState<string>("");
  
  // Detail form states
  const [newDetailName, setNewDetailName] = useState<string>("");
  const [newDetailType, setNewDetailType] = useState<"text" | "string" | "number" | "list" | "">("");
  const [newListItems, setNewListItems] = useState<string[]>([]);
  const [newDetailDescription, setNewDetailDescription] = useState<string>("");
  const [detailNameError, setDetailNameError] = useState<string>("");
  const [editingDetailIndex, setEditingDetailIndex] = useState<number | null>(null);
  const [editingOriginalDetail, setEditingOriginalDetail] = useState<AccountGroupDetailField | null>(null);
  
  // Account dialog states
  const [editAccountDialogOpen, setEditAccountDialogOpen] = useState<boolean>(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState<boolean>(false);
  
  // Account form states
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [accountFormData, setAccountFormData] = useState<{
    accountName: string;
    group: string;
    amount: string;
    currency: string;
    description: string;
    details?: AccountDetailValue;
  }>({
    accountName: "",
    group: "",
    amount: "",
    currency: "USD",
    description: "",
    details: {},
  });
  const [accountNameError, setAccountNameError] = useState<string>("");
  const [isSavingAccount, setIsSavingAccount] = useState<boolean>(false);
  const [creatingAccountName, setCreatingAccountName] = useState<string | null>(null);
  const [showAccountDetails, setShowAccountDetails] = useState<boolean>(false);
  const [initialShowAccountDetails, setInitialShowAccountDetails] = useState<boolean>(false);
  const [expandedAccountDetails, setExpandedAccountDetails] = useState<Set<string>>(new Set());
  const [initialAccountFormData, setInitialAccountFormData] = useState<{
    accountName: string;
    group: string;
    amount: string;
    currency: string;
    description: string;
    details?: AccountDetailValue;
  } | null>(null);
  
  // Account menu states
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  
  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarElement, setSnackbarElement] = useState<React.ReactElement | null>(null);

  return {
    // Group dialog states
    addDialogOpen,
    setAddDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    
    // Group form states
    editingGroup,
    setEditingGroup,
    newGroupName,
    setNewGroupName,
    newGroupDescription,
    setNewGroupDescription,
    groupNameError,
    setGroupNameError,
    editGroupName,
    setEditGroupName,
    editGroupDescription,
    setEditGroupDescription,
    groupToDelete,
    setGroupToDelete,
    isAddingGroup,
    setIsAddingGroup,
    
    // Group menu states
    groupMenuAnchor,
    setGroupMenuAnchor,
    selectedGroup,
    setSelectedGroup,
    selectedGroupInUse,
    setSelectedGroupInUse,
    
    // Group details dialog states
    detailsDialogOpen,
    setDetailsDialogOpen,
    editingGroupDetails,
    setEditingGroupDetails,
    originalGroupDetails,
    setOriginalGroupDetails,
    editingGroupName,
    setEditingGroupName,
    
    // Detail form states
    newDetailName,
    setNewDetailName,
    newDetailType,
    setNewDetailType,
    newListItems,
    setNewListItems,
    newDetailDescription,
    setNewDetailDescription,
    detailNameError,
    setDetailNameError,
    editingDetailIndex,
    setEditingDetailIndex,
    editingOriginalDetail,
    setEditingOriginalDetail,
    
    // Account dialog states
    editAccountDialogOpen,
    setEditAccountDialogOpen,
    deleteAccountDialogOpen,
    setDeleteAccountDialogOpen,
    
    // Account form states
    editingAccount,
    setEditingAccount,
    accountFormData,
    setAccountFormData,
    accountNameError,
    setAccountNameError,
    isSavingAccount,
    setIsSavingAccount,
    creatingAccountName,
    setCreatingAccountName,
    showAccountDetails,
    setShowAccountDetails,
    initialShowAccountDetails,
    setInitialShowAccountDetails,
    expandedAccountDetails,
    setExpandedAccountDetails,
    initialAccountFormData,
    setInitialAccountFormData,
    
    // Account menu states
    accountMenuAnchor,
    setAccountMenuAnchor,
    selectedAccount,
    setSelectedAccount,
    
    // Snackbar states
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    setSnackbarMessage,
    snackbarElement,
    setSnackbarElement,
  };
};
